import React, { useState, useEffect } from 'react';
import Network from '../../../models/Network.js';
import URLS from '../../../urls.js';
import './Module.css';

const AnalysisResultsModule = ({ user, reportId, isSmallScreen }) => {
    const [analysisData, setAnalysisData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const loadAnalysisResults = async () => {
            try {
                setLoading(true);
                setError(null);
                
                // Fetch findings from database via API
                const url = URLS.GET_ONE_REPORT_FINDINGS;
                const dict = {
                    uid: user.uid,
                    report_id: reportId,
                    CLIENT_ACCESS_KEY: process.env.REACT_APP_CLIENT_ACCESS_KEY
                };

                await Network.post(url, dict, (response) => {
                    if (Network.checkValidStatusAndResponse(response)) {
                        setAnalysisData(response.data);
                    } else {
                        console.error('Error fetching analysis results:', response.status);
                        setError('Unable to load analysis results. Please ensure Agent2 analysis has been completed.');
                    }
                });
            } catch (err) {
                console.error('Error loading analysis results:', err);
                setError('Unable to load analysis results. Please ensure Agent2 analysis has been completed.');
            } finally {
                setLoading(false);
            }
        };

        if (reportId && user) {
            loadAnalysisResults();
        }
    }, [reportId, user]);

    const getSeverityColor = (severity) => {
        switch (severity?.toLowerCase()) {
            case 'critical':
                return '#dc3545';
            case 'moderate':
                return '#ffc107';
            case 'minor':
                return '#17a2b8';
            default:
                return '#6c757d';
        }
    };

    const getSeverityIcon = (severity) => {
        switch (severity?.toLowerCase()) {
            case 'critical':
                return '🚨';
            case 'moderate':
                return '⚠️';
            case 'minor':
                return 'ℹ️';
            default:
                return '❓';
        }
    };

    const getComplianceColor = (compliance) => {
        switch (compliance?.toLowerCase()) {
            case 'compliant':
                return '#28a745';
            case 'non-compliant':
                return '#dc3545';
            case 'partially compliant':
                return '#ffc107';
            default:
                return '#6c757d';
        }
    };

    if (loading) {
        return (
            <div className={isSmallScreen ? "standard-module-screen-small" : "standard-module"}>
                <h2 className='module-title'>Analysis Results</h2>
                <div style={{ textAlign: 'center', padding: '20px' }}>
                    <div>Loading analysis results...</div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={isSmallScreen ? "standard-module-screen-small" : "standard-module"}>
                <h2 className='module-title'>Analysis Results</h2>
                <div style={{ textAlign: 'center', padding: '20px', color: '#dc3545' }}>
                    <div>{error}</div>
                </div>
            </div>
        );
    }

    if (!analysisData) {
        return (
            <div className={isSmallScreen ? "standard-module-screen-small" : "standard-module"}>
                <h2 className='module-title'>Analysis Results</h2>
                <div style={{ textAlign: 'center', padding: '20px', color: '#6c757d' }}>
                    <div>No analysis results available</div>
                </div>
            </div>
        );
    }

    const { analysisResults, timestamp, reportId: storedReportId } = analysisData;

    return (
        <div className={isSmallScreen ? "standard-module-screen-small" : "standard-module"}>
            <h2 className='module-title'>Analysis Results</h2>
            
            {/* Analysis Overview */}
            <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <h3 style={{ margin: 0, color: '#495057' }}>Analysis Overview</h3>
                    <span style={{ fontSize: '0.9em', color: '#6c757d' }}>
                        {new Date(timestamp).toLocaleString()}
                    </span>
                </div>
                
                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ 
                            fontSize: '24px', 
                            fontWeight: 'bold',
                            color: getComplianceColor(analysisResults.overall_compliance)
                        }}>
                            {analysisResults.overall_compliance?.toUpperCase() || 'UNKNOWN'}
                        </div>
                        <div style={{ fontSize: '0.9em', color: '#6c757d' }}>Overall Compliance</div>
                    </div>
                    
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#495057' }}>
                            {analysisResults.statistics?.total_findings || 0}
                        </div>
                        <div style={{ fontSize: '0.9em', color: '#6c757d' }}>Total Findings</div>
                    </div>
                    
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#dc3545' }}>
                            {analysisResults.statistics?.critical_findings || 0}
                        </div>
                        <div style={{ fontSize: '0.9em', color: '#6c757d' }}>Critical</div>
                    </div>
                    
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ffc107' }}>
                            {analysisResults.statistics?.moderate_findings || 0}
                        </div>
                        <div style={{ fontSize: '0.9em', color: '#6c757d' }}>Moderate</div>
                    </div>
                    
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#17a2b8' }}>
                            {analysisResults.statistics?.minor_findings || 0}
                        </div>
                        <div style={{ fontSize: '0.9em', color: '#6c757d' }}>Minor</div>
                    </div>
                </div>
            </div>

            {/* Summary */}
            {analysisResults.summary && (
                <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#e9ecef', borderRadius: '8px' }}>
                    <h4 style={{ margin: '0 0 10px 0', color: '#495057' }}>Summary</h4>
                    <p style={{ margin: 0, lineHeight: '1.5', color: '#495057' }}>
                        {analysisResults.summary}
                    </p>
                </div>
            )}

            {/* Findings */}
            {analysisResults.findings && analysisResults.findings.length > 0 ? (
                <div>
                    <h4 style={{ margin: '0 0 15px 0', color: '#495057' }}>Detailed Findings</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                        {analysisResults.findings.map((finding, index) => (
                            <div 
                                key={finding.id || index}
                                style={{ 
                                    border: `2px solid ${getSeverityColor(finding.severity)}`,
                                    borderRadius: '8px',
                                    padding: '15px',
                                    backgroundColor: '#fff'
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                                    <span style={{ fontSize: '20px', marginRight: '10px' }}>
                                        {getSeverityIcon(finding.severity)}
                                    </span>
                                    <h5 style={{ 
                                        margin: 0, 
                                        color: getSeverityColor(finding.severity),
                                        textTransform: 'uppercase'
                                    }}>
                                        {finding.severity} - {finding.type}
                                    </h5>
                                </div>
                                
                                <div style={{ marginBottom: '10px' }}>
                                    <strong style={{ color: '#495057' }}>Issue:</strong>
                                    <p style={{ margin: '5px 0 0 0', lineHeight: '1.4' }}>
                                        {finding.issue}
                                    </p>
                                </div>
                                
                                <div style={{ marginBottom: '10px' }}>
                                    <strong style={{ color: '#495057' }}>Protocol:</strong>
                                    <p style={{ margin: '5px 0 0 0', lineHeight: '1.4' }}>
                                        {finding.protocol}
                                    </p>
                                </div>
                                
                                <div>
                                    <strong style={{ color: '#495057' }}>Evidence:</strong>
                                    <p style={{ margin: '5px 0 0 0', lineHeight: '1.4' }}>
                                        {finding.evidence}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                <div style={{ 
                    textAlign: 'center', 
                    padding: '20px', 
                    backgroundColor: '#d4edda', 
                    borderRadius: '8px',
                    border: '1px solid #c3e6cb'
                }}>
                    <div style={{ fontSize: '18px', color: '#155724', marginBottom: '5px' }}>
                        ✅ No Findings Identified
                    </div>
                    <div style={{ color: '#155724' }}>
                        The report appears to be compliant with all applicable protocols.
                    </div>
                </div>
            )}
        </div>
    );
};

export default AnalysisResultsModule;
